/**
 * S3-compatible cloud provider. Ported from yaqatw's src/service/cloud/s3.ts.
 *
 * Unlike the OAuth providers, S3 signs every request with AWS Signature V4
 * (see ./sigv4) using bring-your-own credentials the user enters in
 * Settings > Backend. There is no token, no refresh, and no auth dialog — so
 * this class overrides the BaseCloudProvider auth machinery and only reuses
 * its request throttling.
 *
 * Path model: S3 has no folders. App paths map directly to object keys with
 * "/" as the separator (lowercased, matching the Google Drive provider). URLs
 * are path-style (`https://endpoint/bucket/key`) for max compatibility with
 * non-AWS endpoints. Unlike the other 4 providers, there is deliberately no
 * "YaqatW-DATA" root-folder prefix here — the bucket itself is the app
 * boundary (matches yaqatw's source exactly; verified before porting).
 *
 * NOTE: because this runs in a browser, the target bucket MUST have a CORS
 * policy allowing this app's origin and the signed headers (Authorization,
 * x-amz-date, x-amz-content-sha256). That is a bucket-side prerequisite no
 * code here can satisfy.
 */

import Events from "./events";
import { BaseCloudProvider } from "./BaseCloudProvider";
import { signRequest } from "./sigv4";
import { maxRetryAttempts } from "./retryPolicy";
import type { FileMetadata, PushResult, UploadResult } from "./types";
import { getSettingValue } from "../settings/settingsStore";
import { useSettingsStore } from "../settings/settingsStore";

function getBackendSetting(key: string): string {
  const value = getSettingValue("backend", key);
  return typeof value === "string" ? value : "";
}

interface S3Credentials {
  endpoint: string; // scheme + host, no trailing slash
  region: string;
  bucketName: string;
  accessKeyId: string;
  secretAccessKey: string;
}

interface ListedObject {
  key: string;
  lastModified: string;
}

class S3ProviderClass extends BaseCloudProvider {
  public readonly tokenName = "s3Credentials";
  protected readonly providerName = "S3";

  // S3-compatible endpoints rarely throttle small workloads; keep a gentle floor.
  protected readonly MIN_REQUEST_INTERVAL_MS = 100;

  private cachedCreds: S3Credentials | null = null;
  private verified = false;

  constructor() {
    super();
    // Settings edits invalidate the credential cache so the next request re-reads.
    Events.on("settingsUpdated", () => {
      this.cachedCreds = null;
    });
  }

  // ── Credentials ───────────────────────────────────────────────────────────

  private async loadCredentials(): Promise<S3Credentials | null> {
    if (this.cachedCreds) return this.cachedCreds;
    const endpoint = getBackendSetting("s3Endpoint").replace(/\/+$/, "");
    const region = getBackendSetting("s3Region") || "us-east-1";
    const bucketName = getBackendSetting("s3BucketName");
    const accessKeyId = getBackendSetting("s3AccessKeyId");
    const secretAccessKey = getBackendSetting("s3SecretAccessKey");

    if (!endpoint || !bucketName || !accessKeyId || !secretAccessKey) return null;

    this.cachedCreds = { endpoint, region, bucketName, accessKeyId, secretAccessKey };
    return this.cachedCreds;
  }

  private async requireCredentials(): Promise<S3Credentials> {
    const creds = await this.loadCredentials();
    if (!creds) {
      const error =
        "S3 credentials are incomplete. Set endpoint, region, bucket, access key and secret in Settings.";
      Events.emit("showError", error);
      throw new Error(error);
    }
    return creds;
  }

  // ── Auth surface (no OAuth) ─────────────────────────────────────────────────

  protected getAuthUrl(): string {
    return ""; // unused — S3 has no authorize URL.
  }

  protected async getAccessToken(): Promise<string> {
    throw new Error("S3 provider does not use bearer tokens.");
  }

  /**
   * "Test Connection" entry point. Re-reads freshly saved settings and verifies
   * the bucket is reachable with a 1-key ListObjectsV2. Returns "ok" on success.
   */
  protected async performAuthenticate(): Promise<string> {
    this.cachedCreds = null;
    const creds = await this.loadCredentials();
    if (!creds) {
      const error =
        "S3 credentials are incomplete. Set endpoint, region, bucket, access key and secret in Settings.";
      Events.emit("showError", error);
      throw new Error(error);
    }
    try {
      const res = await this.s3Fetch("GET", "", { query: "list-type=2&max-keys=1" });
      if (!res.ok) throw new Error(`Bucket check failed with status ${res.status}`);
      this.verified = true;
      Events.emit("showStatus", { intent: "success", text: "S3 connection successful." });
      return "ok";
    } catch (error: any) {
      this.verified = false;
      Events.emit("showError", `S3 connection failed: ${error?.message || String(error)}`);
      throw error;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    return this.verified && !!(await this.loadCredentials());
  }

  /**
   * S3 has no account identity (it's bring-your-own bucket credentials, not a
   * per-user account), so collaboration falls back to a device id — see
   * src/collab/identity.ts.
   */
  public async getIdentity(): Promise<string | null> {
    return null;
  }

  async disconnect(): Promise<void> {
    for (const key of [
      "s3Endpoint",
      "s3Region",
      "s3BucketName",
      "s3AccessKeyId",
      "s3SecretAccessKey",
    ]) {
      useSettingsStore.getState().setValue("backend", key, "");
    }
    this.cachedCreds = null;
    this.verified = false;
  }

  // ── Signed fetch ────────────────────────────────────────────────────────────

  private buildUrl(creds: S3Credentials, key: string, query?: string): string {
    let url = `${creds.endpoint}/${encodeURIComponent(creds.bucketName)}`;
    if (key) url += "/" + key.split("/").map(encodeURIComponent).join("/");
    if (query) url += `?${query}`;
    return url;
  }

  /**
   * SigV4-signed fetch. Returns the Response (caller checks `.ok`, mirroring the
   * base fetchWithAuth contract). Retries 429/503 with exponential backoff.
   */
  private async s3Fetch(
    method: string,
    key: string,
    opts: { body?: Uint8Array | string; query?: string; contentType?: string } = {},
    retryCount = 0
  ): Promise<Response> {
    const creds = await this.requireCredentials();
    await this.throttle();

    const url = this.buildUrl(creds, key, opts.query);
    const bodyBytes =
      opts.body === undefined
        ? undefined
        : typeof opts.body === "string"
          ? new TextEncoder().encode(opts.body)
          : opts.body;

    const signed = await signRequest({
      method,
      url,
      region: creds.region,
      service: "s3",
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      body: bodyBytes,
    });

    const headers: Record<string, string> = { ...signed };
    if (opts.contentType) headers["Content-Type"] = opts.contentType;

    const response = await fetch(url, {
      method,
      headers,
      body: bodyBytes as BodyInit | undefined,
      signal: AbortSignal.timeout(this.FETCH_TIMEOUT_MS),
    });

    if ((response.status === 429 || response.status === 503) && retryCount <= maxRetryAttempts()) {
      const retryAfter = response.headers.get("Retry-After");
      const waitTime = retryAfter
        ? parseInt(retryAfter, 10) * 1000
        : 2000 * Math.pow(2, retryCount);
      console.warn(`[S3] ${response.status} throttled. Retrying in ${waitTime}ms...`);
      await new Promise((r) => setTimeout(r, waitTime));
      return this.s3Fetch(method, key, opts, retryCount + 1);
    }

    return response;
  }

  // ── Path helpers (trivial for S3) ────────────────────────────────────────────

  /** Folder paths are key prefixes; normalised to lowercase with a trailing "/". */
  async resolveFolderPath(fullPath: string): Promise<string> {
    return this.toPrefix(fullPath);
  }

  /** A file path is simply its object key. */
  async resolveFilePath(fullPath: string): Promise<{ parentFolderId: string; fileName: string }> {
    const key = fullPath.toLowerCase();
    const parts = key.split("/").filter((p) => p);
    const fileName = parts.pop() || "";
    return { parentFolderId: parts.join("/"), fileName };
  }

  private toPrefix(path: string): string {
    const normalized = path.toLowerCase().replace(/^\/+|\/+$/g, "");
    return normalized ? `${normalized}/` : "";
  }

  // ── Listing ──────────────────────────────────────────────────────────────────

  private async listObjects(prefix: string, delimiter?: string): Promise<ListedObject[]> {
    const results: ListedObject[] = [];
    let continuationToken: string | undefined;

    do {
      let query = `list-type=2&prefix=${encodeURIComponent(prefix)}`;
      if (delimiter) query += `&delimiter=${encodeURIComponent(delimiter)}`;
      if (continuationToken)
        query += `&continuation-token=${encodeURIComponent(continuationToken)}`;

      const res = await this.s3Fetch("GET", "", { query });
      if (!res.ok) throw new Error(`List failed with status ${res.status}`);

      const xml = new DOMParser().parseFromString(await res.text(), "application/xml");
      for (const node of Array.from(xml.getElementsByTagName("Contents"))) {
        const key = node.getElementsByTagName("Key")[0]?.textContent || "";
        const lastModified = node.getElementsByTagName("LastModified")[0]?.textContent || "";
        if (key && !key.endsWith("/")) results.push({ key, lastModified });
      }

      const truncated = xml.getElementsByTagName("IsTruncated")[0]?.textContent === "true";
      continuationToken = truncated
        ? xml.getElementsByTagName("NextContinuationToken")[0]?.textContent || undefined
        : undefined;
    } while (continuationToken);

    return results;
  }

  private static isoTime(value: string): string {
    if (!value) throw new Error("isoTime called with empty value");
    return new Date(value).toISOString();
  }

  /**
   * After a PUT, fetch the object's canonical server-side LastModified via
   * ListObjectsV2 (XML body, unaffected by CORS header exposure). Returns
   * `null` if the listing doesn't surface the exact key (eventual consistency
   * on non-AWS endpoints) — callers should fall back to client time only then.
   */
  private async fetchServerLastModified(key: string): Promise<string | null> {
    try {
      const query = `list-type=2&prefix=${encodeURIComponent(key)}&max-keys=1`;
      const res = await this.s3Fetch("GET", "", { query });
      if (!res.ok) return null;
      const xml = new DOMParser().parseFromString(await res.text(), "application/xml");
      for (const node of Array.from(xml.getElementsByTagName("Contents"))) {
        const k = node.getElementsByTagName("Key")[0]?.textContent || "";
        if (k === key) {
          return node.getElementsByTagName("LastModified")[0]?.textContent || null;
        }
      }
      return null;
    } catch (err) {
      console.warn(`[S3] fetchServerLastModified failed for ${key}:`, err);
      return null;
    }
  }

  // ── Provider interface ─────────────────────────────────────────────────────

  public async push(fileName: string, data: any): Promise<PushResult | void> {
    const key = fileName.toLowerCase();
    try {
      const res = await this.s3Fetch("PUT", key, {
        body: JSON.stringify(data),
        contentType: "application/json",
      });
      if (!res.ok) throw new Error(`Upload failed with status ${res.status}`);
      // PUT's Last-Modified header is usually hidden by CORS, so read the
      // canonical server time back from ListObjectsV2 (XML body). If that
      // listing doesn't yet surface the key, fall back to client time.
      const serverLm = await this.fetchServerLastModified(key);
      return {
        modifiedTime: serverLm ? S3ProviderClass.isoTime(serverLm) : new Date().toISOString(),
      };
    } catch (error) {
      console.error(`Error uploading ${fileName}:`, error);
      throw error;
    }
  }

  public async pushDiff(globalEntries: any[]): Promise<void> {
    for (const entry of globalEntries) {
      await this.push(entry.path, entry.data);
      Events.emit("showStatus", { intent: "info", text: `Synced ${entry.path} to S3.` });
    }
  }

  public async pull(itemPath: string, fileId_: string | null = null): Promise<any> {
    const key = fileId_ ? fileId_ : itemPath.toLowerCase();
    try {
      const res = await this.s3Fetch("GET", key);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
      const text = await res.text();
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (parseError) {
        console.warn(`[S3] Failed to parse JSON for ${itemPath}:`, parseError);
        return null;
      }
    } catch (error) {
      console.error(`Error pulling ${itemPath}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async pullById(fileId: string): Promise<any> {
    return this.pull(fileId, fileId);
  }

  public async getMetadata(path: string): Promise<FileMetadata[]> {
    try {
      const objects = await this.listObjects(this.toPrefix(path), "/");
      return objects.map((o) => {
        const name = o.key.split("/").pop() || o.key;
        return {
          id: o.key,
          name,
          path: name,
          modifiedTime: o.lastModified
            ? S3ProviderClass.isoTime(o.lastModified)
            : new Date(0).toISOString(),
        };
      });
    } catch (error) {
      console.error(`Error getting metadata for ${path}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async getMetadataAll(baseFolder: string): Promise<FileMetadata[]> {
    try {
      const objects = await this.listObjects(this.toPrefix(baseFolder));
      return objects.map((o) => ({
        id: o.key,
        name: o.key.split("/").pop() || o.key,
        path: o.key,
        modifiedTime: o.lastModified
          ? S3ProviderClass.isoTime(o.lastModified)
          : new Date(0).toISOString(),
      }));
    } catch (error) {
      console.error(`Error getting metadata for all files under ${baseFolder}:`, error);
      Events.emit("showError", `Sync failed: ${(error as any).message}`);
      throw error;
    }
  }

  public async uploadFile(
    filePath: string,
    fileContent: Blob | string | ArrayBuffer
  ): Promise<UploadResult> {
    const key = filePath.toLowerCase();
    try {
      let body: Uint8Array;
      let contentType = "application/octet-stream";

      if (fileContent instanceof Blob) {
        body = new Uint8Array(await fileContent.arrayBuffer());
        contentType = fileContent.type || contentType;
      } else if (fileContent instanceof ArrayBuffer) {
        body = new Uint8Array(fileContent);
      } else {
        if (key.endsWith(".json")) contentType = "application/json";
        else if (key.endsWith(".csv")) contentType = "text/csv";
        else if (key.endsWith(".html") || key.endsWith(".htm")) contentType = "text/html";
        else if (key.endsWith(".xml")) contentType = "application/xml";
        else contentType = "text/plain";
        body = new TextEncoder().encode(fileContent);
      }

      const res = await this.s3Fetch("PUT", key, { body, contentType });
      if (!res.ok) throw new Error(`Upload failed with status ${res.status}`);

      const creds = await this.requireCredentials();
      return { url: this.buildUrl(creds, key) };
    } catch (error: any) {
      console.error(`Error uploading file ${filePath}:`, error);
      Events.emit("showError", `File upload failed: ${error.message}`);
      throw error;
    }
  }

  public async downloadFile(filePath: string): Promise<Blob> {
    const res = await this.s3Fetch("GET", filePath.toLowerCase());
    if (!res.ok) throw new Error(`S3 download failed for ${filePath}: ${res.status}`);
    return res.blob();
  }

  public async delete(filePath: string): Promise<void> {
    try {
      const res = await this.s3Fetch("DELETE", filePath.toLowerCase());
      if (!res.ok && res.status !== 404) {
        throw new Error(`Delete failed with status ${res.status}`);
      }
    } catch (error) {
      console.error(`Error deleting ${filePath}:`, error);
      Events.emit("showError", `Delete failed: ${(error as any).message}`);
      throw error;
    }
  }
}

const S3Provider = new S3ProviderClass();
export default S3Provider;
